import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "cmdk";
import {
  Search,
  Clock,
  LayoutDashboard,
  CalendarCheck,
  Images,
  MessageSquare,
  FolderTree,
  Package,
  HelpCircle,
  Settings as SettingsIcon,
  LogOut,
  Plus,
} from "lucide-react";

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  // Menu items dengan ikon dan deskripsi
  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      description: "Overview dan statistik",
      path: "/admin",
      category: "pages",
    },
    {
      id: "bookings",
      label: "Booking Management",
      icon: CalendarCheck,
      description: "Kelola booking dan pembayaran",
      path: "/admin/bookings",
      category: "pages",
    },
    {
      id: "gallery",
      label: "Gallery",
      icon: Images,
      description: "Kelola foto galeri",
      path: "/admin/gallery",
      category: "pages",
    },
    {
      id: "testimonials",
      label: "Testimoni",
      icon: MessageSquare,
      description: "Kelola testimoni klien",
      path: "/admin/testimonials",
      category: "pages",
    },
    {
      id: "categories",
      label: "Kategori",
      icon: FolderTree,
      description: "Kelola kategori layanan",
      path: "/admin/categories",
      category: "pages",
    },
    {
      id: "packages",
      label: "Layanan & Paket",
      icon: Package,
      description: "Kelola paket henna",
      path: "/admin/packages",
      category: "pages",
    },
    {
      id: "faq",
      label: "FAQ",
      icon: HelpCircle,
      description: "Kelola pertanyaan umum",
      path: "/admin/faq",
      category: "pages",
    },
    {
      id: "settings",
      label: "Pengaturan",
      icon: SettingsIcon,
      description: "Konfigurasi website",
      path: "/admin/settings",
      category: "settings",
    },
  ];

  // Quick actions
  const actions = [
    {
      id: "add-booking",
      label: "Booking Baru",
      icon: Plus,
      description: "Buat booking baru",
      action: () => {
        navigate("/admin/bookings");
        setTimeout(() => {
          // Trigger add booking modal jika ada
          const event = new CustomEvent("open-add-booking");
          window.dispatchEvent(event);
        }, 100);
      },
      category: "actions",
    },
    {
      id: "add-gallery",
      label: "Upload Foto",
      icon: Images,
      description: "Tambah foto ke galeri",
      action: () => {
        navigate("/admin/gallery");
        setTimeout(() => {
          const event = new CustomEvent("open-add-gallery");
          window.dispatchEvent(event);
        }, 100);
      },
      category: "actions",
    },
    {
      id: "add-testimonial",
      label: "Testimoni Baru",
      icon: MessageSquare,
      description: "Tambah testimoni",
      action: () => {
        navigate("/admin/testimonials");
        setTimeout(() => {
          const event = new CustomEvent("open-add-testimonial");
          window.dispatchEvent(event);
        }, 100);
      },
      category: "actions",
    },
    {
      id: "add-package",
      label: "Paket Baru",
      icon: Package,
      description: "Buat paket layanan",
      action: () => {
        navigate("/admin/packages");
        setTimeout(() => {
          const event = new CustomEvent("open-add-package");
          window.dispatchEvent(event);
        }, 100);
      },
      category: "actions",
    },
  ];

  // System commands
  const systemCommands = [
    {
      id: "logout",
      label: "Keluar",
      icon: LogOut,
      description: "Logout dari akun admin",
      action: () => {
        const event = new CustomEvent("admin-logout");
        window.dispatchEvent(event);
        onClose();
      },
      category: "system",
    },
  ];

  // Load recent searches dari localStorage
  const [recentSearches, setRecentSearches] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem("admin-recent-searches");
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Handle item selection
  const handleSelect = useCallback(
    (callback) => {
      callback();
      onClose();
    },
    [onClose],
  );

  // Navigate to page
  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose],
  );

  // Save search to recent
  const handleSearchChange = useCallback(
    (value) => {
      setSearch(value);
      if (value && value.length > 0) {
        const newRecent = [
          value,
          ...recentSearches.filter((s) => s !== value),
        ].slice(0, 5);
        setRecentSearches(newRecent);
        localStorage.setItem(
          "admin-recent-searches",
          JSON.stringify(newRecent),
        );
      }
    },
    [recentSearches],
  );

  // Filter items berdasarkan search
  const filteredMenuItems = search
    ? menuItems.filter(
        (item) =>
          item.label.toLowerCase().includes(search.toLowerCase()) ||
          item.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  const filteredActions = search
    ? actions.filter(
        (item) =>
          item.label.toLowerCase().includes(search.toLowerCase()) ||
          item.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  const filteredSystem = search
    ? systemCommands.filter(
        (item) =>
          item.label.toLowerCase().includes(search.toLowerCase()) ||
          item.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Modal Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Command Palette Modal */}
      <div className="absolute inset-0 flex items-start justify-center pt-[15vh] px-4 pointer-events-none">
        <Command
          loop
          shouldFilter={false}
          onKeyDown={(e) => {
            if (e.key === "escape") {
              e.stopPropagation();
              onClose();
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl rounded-lg border border-[#D4AF7A]/30 bg-[#0A0A0A] shadow-2xl shadow-black/50 pointer-events-auto"
        >
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-[#D4AF7A]/20 px-4 py-3">
            <Search className="w-5 h-5 text-[#D4AF7A]" />
            <CommandInput
              placeholder="Cari menu, fitur, atau navigasi..."
              value={search}
              onValueChange={handleSearchChange}
              className="flex-1 text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none bg-transparent"
            />
            <div className="text-xs text-[#F7F5F2]/50 hidden sm:block">ESC</div>
          </div>

          <CommandList className="max-h-[400px] overflow-y-auto px-3 py-2">
            {!search && (
              <>
                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <CommandGroup heading="Pencarian Terakhir">
                    {recentSearches.map((item) => (
                      <CommandItem
                        key={item}
                        onSelect={() => {
                          setSearch(item);
                        }}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-[#F7F5F2] hover:bg-white/10 cursor-pointer"
                      >
                        <Clock className="w-4 h-4 text-[#D4AF7A]" />
                        <span>{item}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}

            {/* Pages */}
            {(filteredMenuItems.length > 0 || !search) && (
              <CommandGroup
                heading={<span className="text-[#D4AF7A]">📊 Halaman</span>}
              >
                {(search ? filteredMenuItems : menuItems).map((item) => {
                  const Icon = item.icon;
                  return (
                    <CommandItem
                      key={item.id}
                      onSelect={() => handleNavigate(item.path)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-[#F7F5F2] hover:bg-white/10 cursor-pointer"
                    >
                      <Icon className="w-5 h-5 text-[#D4AF7A]" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{item.label}</div>
                        <div className="text-xs text-[#F7F5F2]/60">
                          {item.description}
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* Actions */}
            {(filteredActions.length > 0 || !search) && (
              <CommandGroup
                heading={<span className="text-[#D4AF7A]">⚡ Aksi Cepat</span>}
              >
                {(search ? filteredActions : actions).map((item) => {
                  const Icon = item.icon;
                  return (
                    <CommandItem
                      key={item.id}
                      onSelect={() => handleSelect(item.action)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-[#F7F5F2] hover:bg-white/10 cursor-pointer"
                    >
                      <Plus className="w-5 h-5 text-emerald-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{item.label}</div>
                        <div className="text-xs text-[#F7F5F2]/60">
                          {item.description}
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* System */}
            {(filteredSystem.length > 0 || !search) && (
              <CommandGroup
                heading={<span className="text-[#D4AF7A]">⚙️ Sistem</span>}
              >
                {(search ? filteredSystem : systemCommands).map((item) => {
                  const Icon = item.icon;
                  return (
                    <CommandItem
                      key={item.id}
                      onSelect={() => handleSelect(item.action)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-[#F7F5F2] hover:bg-white/10 cursor-pointer"
                    >
                      <Icon className="w-5 h-5 text-red-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{item.label}</div>
                        <div className="text-xs text-[#F7F5F2]/60">
                          {item.description}
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* Empty State */}
            {search &&
              filteredMenuItems.length === 0 &&
              filteredActions.length === 0 &&
              filteredSystem.length === 0 && (
                <CommandEmpty className="py-12 text-center text-[#F7F5F2]/60">
                  <div className="flex flex-col items-center gap-3">
                    <Search className="w-12 h-12 text-[#D4AF7A]/30 mx-auto" />
                    <p>Tidak ada hasil untuk "{search}"</p>
                    <p className="text-sm">Coba kata kunci lain</p>
                  </div>
                </CommandEmpty>
              )}
          </CommandList>

          {/* Footer - Keyboard hint */}
          <div className="border-t border-[#D4AF7A]/20 px-4 py-2">
            <div className="flex items-center justify-between text-xs text-[#F7F5F2]/50">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 bg-white/10 rounded text-[10px]">
                    ↑↓
                  </kbd>
                  <span>Navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 bg-white/10 rounded text-[10px]">
                    ↵
                  </kbd>
                  <span>Select</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 bg-white/10 rounded text-[10px]">
                    esc
                  </kbd>
                  <span>Close</span>
                </span>
              </div>
              <div className="hidden sm:block">
                <span>⌨️ Tip: Gunakan Ctrl/Cmd + K untuk buka kapan saja</span>
              </div>
            </div>
          </div>
        </Command>
      </div>
    </div>
  );
};

export default CommandPalette;
